import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import i18n from './i18n';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext, canAccessAccounting } from './context/AuthContext';
import { updateUserPreferences } from './services/api';
import { UNITS_CHANGE_EVENT, isUnitSystem, readUnitSystem, writeUnitSystem } from './services/unitPreference';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import MainLayout from './components/MainLayout';
import LandingPage from './pages/LandingPage';
import EnvironmentalLandingPage from './pages/EnvironmentalLandingPage';
import AccountingLandingPage from './pages/AccountingLandingPage';
import Dashboard from './pages/Dashboard';
import History from './pages/History';
import PlaceholderPage from './pages/PlaceholderPage';
import GPSLicenses from './pages/GPSLicenses';
import GpsRovers from './pages/GpsRovers';
import LoginPage from './pages/LoginPage';
import ToolsLandingPage from './pages/ToolsLandingPage';
import { OmniboxJsonConverterProvider } from './context/OmniboxJsonConverterContext';
import { OmniboxCsvConverterProvider } from './context/OmniboxCsvConverterContext';
import OmniboxJsonConverterPage from './pages/OmniboxJsonConverterPage';
import OmniboxCsvConverterPage from './pages/OmniboxCsvConverterPage';
import FeedbackPage from './pages/FeedbackPage';
import QrFeedbackPage from './pages/QrFeedbackPage';
import JobSitesPage from './pages/JobSitesPage';
import DataLoggersPage from './pages/DataLoggersPage';
import LoadTestDashboard from './pages/LoadTestDashboard';
import LoadTestLandingPage from './pages/LoadTestLandingPage';
import LoadTestHistoryPage from './pages/LoadTestHistoryPage';
import LoadTestHistoryTestPage from './pages/LoadTestHistoryTestPage';
import LoadTestPastTestDashboard from './pages/LoadTestPastTestDashboard';
import EquipmentMapPage from './pages/EquipmentMapPage';
import { LoadTestDashboardProvider } from './context/LoadTestContext';
import ManageUsersPage from './pages/ManageUsersPage';

import {
  MachinesPlaceholder,
  OmniboxPlaceholder,
  GpsSystemsPlaceholder,
  JobSitesPlaceholder,
  ToolsPlaceholder,
  LoadTestingPlaceholder,
  EnvironmentalPlaceholder,
  AccountingPlaceholder,
} from './placeholders';

// Default landing page when the user has no explicit HomePage override.
// Admin & Technician → Equipment; Business Unit Admin & Standard → Operations.
const defaultHomeForRole = (role) => {
  if (role === 'standard' || role === 'bu_manager') return '/operations';
  return '/equipment';
};

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('gps-theme');
    return saved === 'dark';
  });

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? 'dark' : 'light',
          primary: {
            main: '#0ea5e9',
          },
          secondary: {
            main: '#003A70',
          },
          error: {
            main: '#e90e0e',
          },
          background: {
            default: darkMode ? '#0a0a0a' : '#f5f5f5',
            paper: darkMode ? '#141414' : '#FFFFFF',
            dashboard: darkMode ? '#292929' : '#f5f5f5',
          },
          divider: darkMode ? '#333333' : 'rgba(0, 0, 0, 0.12)',
          scrollbar: darkMode ? '#444444' : '#aaaaaa'
        },
        typography: {
          fontFamily: 'Lexend, Inter, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
        shape: {
          borderRadius: 10,
        },
        chart: {
          grid: darkMode ? '#333333' : '#e5e7eb',
          axis: darkMode ? '#9ca3af' : '#6b7280',
          tooltipBg: darkMode ? '#2e2e2e': '#f5f5f5',
          tooltipText: darkMode ? '#999999' : '#acacac',
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: darkMode
              ? {
                  body: {
                    scrollbarColor: '#424242 #121212',
                    '&::-webkit-scrollbar': {
                      width: 10,
                      backgroundColor: '#121212',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#121212',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#424242',
                      borderRadius: 8,
                    },
                    '&::-webkit-scrollbar-corner': {
                      backgroundColor: '#121212',
                    },
                  },
                }
              : {},
          },
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow: darkMode
                  ? '0 6px 20px rgba(0,0,0,0.6)'
                  : '0 6px 20px rgba(0,0,0,0.08)',
                ...(darkMode && {
                  border: 'none',
                }),
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: darkMode ? {
                backgroundImage: 'none',
              } : {},
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: darkMode ? {
                backgroundColor: '#21262D',
              } : {},
            },
          },
          MuiButton: {
            styleOverrides: {
              contained: {
                color: darkMode ? '#000000' : '#FFFFFF',
              },
            },
          },
        },
      }),
    [darkMode]
  );

  const [user, setUser] = useState(() => {
    const saved = sessionStorage.getItem('mts-user');
    return saved ? JSON.parse(saved) : null;
  });

  const handleLogin = useCallback((userData) => {
    sessionStorage.setItem('mts-user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('mts-user');
    sessionStorage.removeItem('mts-token');
    setUser(null);
  }, []);

  const handleUpdateUser = useCallback((updates) => {
    const updated = { ...user, ...updates };
    sessionStorage.setItem('mts-user', JSON.stringify(updated));
    setUser(updated);
  }, [user]);

  useEffect(() => {
    const lang = user?.language || 'en';
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [user?.language]);

  // ── Units follow the account ──────────────────────────────────────────────
  // The page-level setting lives in localStorage (services/unitPreference.js).
  // Here it is kept in step with the account: the saved value is applied at
  // sign-in, and any change (Settings dialog, the Load Testing toggle, ...) is
  // saved back. An account with no saved value yet adopts the browser's current
  // choice, so people who picked metric before this existed keep it.
  const userRef = useRef(user);
  userRef.current = user;

  // The value currently being saved, so the same save isn't sent twice before
  // the session user has caught up (React runs mount effects twice in dev).
  const savingUnitsRef = useRef(null);

  const saveUnits = useCallback((units) => {
    const current = userRef.current;
    if (!current?.id || savingUnitsRef.current === units) return;
    savingUnitsRef.current = units;
    const previous = current.units ?? null;
    // Record it on the session user first so repeated flips don't double-save.
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, units };
      sessionStorage.setItem('mts-user', JSON.stringify(updated));
      return updated;
    });
    updateUserPreferences(current.id, { units }).finally(() => {
      if (savingUnitsRef.current === units) savingUnitsRef.current = null;
    }).catch((e) => {
      console.error('Failed to save units preference', e);
      // Leave the page in the chosen units, but let the next change retry.
      setUser((prev) => {
        if (!prev || prev.units !== units) return prev;
        const reverted = { ...prev, units: previous };
        sessionStorage.setItem('mts-user', JSON.stringify(reverted));
        return reverted;
      });
    });
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    if (isUnitSystem(user.units)) {
      if (user.units !== readUnitSystem()) writeUnitSystem(user.units);
    } else {
      saveUnits(readUnitSystem());
    }
  }, [user?.id, user?.units, saveUnits]);

  useEffect(() => {
    const onChange = () => {
      const current = userRef.current;
      const chosen = readUnitSystem();
      if (current?.id && current.units !== chosen) saveUnits(chosen);
    };
    window.addEventListener(UNITS_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(UNITS_CHANGE_EVENT, onChange);
  }, [saveUnits]);

  const isAuthenticated = Boolean(user);

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem('gps-theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  // If not logged in, show login page for all routes
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path='/feedback' element={<MainLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode}><FeedbackPage /></MainLayout>} />

            <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, onLogout: handleLogout, onUpdateUser: handleUpdateUser }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <MainLayout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
            <Routes>
              {/* Default redirect — use user's saved homepage or dashboard */}
              <Route path="/" element={<Navigate to={user.homePage || defaultHomeForRole(user.role)} replace />} />
              <Route path="/login" element={<Navigate to={user.homePage || defaultHomeForRole(user.role)} replace />} />

              {/* Equipment Section */}
              <Route path="/equipment" element={<LandingPage />} />
              <Route path="/equipment/machines" element={<MachinesPlaceholder />} />
              <Route path="/equipment/machines/engine-health" element={<PlaceholderPage title="Engine Health" description="Monitor engine health metrics and diagnostics." />} />
              <Route path="/equipment/machines/sensor-health" element={<PlaceholderPage title="Sensor Health" description="Monitor sensor status and calibration." />} />
              {/* Admins and technicians see the real pages; everyone else still gets the placeholder */}
              <Route path="/equipment/machines/gps-health" element={['admin', 'technician'].includes(user.role) ? <Dashboard /> : <GpsSystemsPlaceholder />} /> //Dashboard
              <Route path="/equipment/machines/gps-health/history" element={['admin', 'technician'].includes(user.role) ? <History /> : <GpsSystemsPlaceholder />} /> //History
              <Route path="/equipment/gps-licenses" element={<GPSLicenses />} />
              {/* Open to every role; adding and editing rovers is limited to admins, BU admins and technicians (in the page and on the API). */}
              <Route path="/equipment/gps-rovers" element={<GpsRovers />} />
              <Route path="/equipment/omnibox" element={<OmniboxPlaceholder />} />
              <Route path="/equipment/omnibox/:id" element={<OmniboxPlaceholder />} />
              {/* Open to every role; adding and editing boxes and loggers is limited to admins, BU admins and technicians (in the page and on the API). */}
              <Route path="/equipment/data-loggers" element={<DataLoggersPage />} />
              <Route path="/equipment/map" element={['admin', 'technician'].includes(user.role) ? <EquipmentMapPage darkMode={darkMode} /> : <PlaceholderPage title="Equipment Map" description="Map of rigs, rovers, job sites and load test boxes." />} />

              {/* Operations Section */}
              <Route path="/operations" element={<Navigate to={'/operations/job-sites'} />} />
              <Route path="/operations/job-sites" element={user.role === 'admin' ? <JobSitesPage /> : <JobSitesPlaceholder/> } />
              <Route path="/operations/tools" element={user.role === 'admin' ? <ToolsLandingPage/> : <ToolsPlaceholder />} />
              <Route
                path="/operations/tools/omnibox-json-converter"
                element={
                  <OmniboxJsonConverterProvider>
                    <OmniboxJsonConverterPage />
                  </OmniboxJsonConverterProvider>
                }
              />
              <Route
                path="/operations/tools/omnibox-csv-converter"
                element={
                  <OmniboxCsvConverterProvider>
                    <OmniboxCsvConverterPage />
                  </OmniboxCsvConverterProvider>
                }
              />
              {/* Load Testing opens on a choice between the live dashboard and past tests.
                  Admins only for now; everyone else sees the Coming Soon page. */}
              <Route
                path="/operations/load-test"
                element={user.role === 'admin' ? <LoadTestLandingPage /> : <LoadTestingPlaceholder />}
              />
              <Route
                path="/operations/load-test/live"
                element={
                  user.role === 'admin' ? (
                    <LoadTestDashboardProvider>
                      <LoadTestDashboard darkMode={darkMode} />
                    </LoadTestDashboardProvider>
                  ) : <LoadTestingPlaceholder />
                }
              />
              <Route
                path="/operations/load-test/history"
                element={user.role === 'admin' ? <LoadTestHistoryPage /> : <LoadTestingPlaceholder />}
              />
              {/* A past test, and its dashboard converted with one of its revisions. */}
              <Route
                path="/operations/load-test/history/:testId"
                element={user.role === 'admin' ? <LoadTestHistoryTestPage /> : <LoadTestingPlaceholder />}
              />
              <Route
                path="/operations/load-test/history/:testId/rev/:revisionId"
                element={
                  user.role === 'admin' ? (
                    <LoadTestDashboardProvider>
                      <LoadTestPastTestDashboard />
                    </LoadTestDashboardProvider>
                  ) : <LoadTestingPlaceholder />
                }
              />
              {/* Calibrations now lives as a tab under Equipment > Load Testing */}
              <Route path="/operations/calibrations" element={<Navigate to="/equipment/data-loggers?tab=calibrations" replace />} />


              {/* Environmental Section */}
              <Route path="/environmental" element={<EnvironmentalPlaceholder />} />

              {/* Accounting Section - accounting and admin roles only */}
              <Route path="/accounting" element={
                canAccessAccounting(user.role) ? <AccountingPlaceholder /> : <Navigate to="/equipment" replace />
              } />
              <Route path="/accounting/tips-converter" element={
                canAccessAccounting(user.role) ? <AccountingPlaceholder /> : <Navigate to="/equipment" replace />
              } />

              {/* Admin Section - admins full, BU admins scoped to their own BU */}
              <Route path="/admin/users" element={
                ['admin', 'bu_manager'].includes(user.role) ? <ManageUsersPage /> : <Navigate to="/equipment" replace />
              } />

              {/* Footer Pages */}
              <Route path='/contact' element={<PlaceholderPage title="Contact" description="" />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/qr-feedback" element={<QrFeedbackPage />} />
            </Routes>
          </MainLayout>
        </Router>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}

export default App;
